import React, { useState } from "react";
import { ChevronLeft, Search, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import {
  useAddCallParticipantMutation,
  useGetMemberAvailabilityQuery,
  useGetOwnMembershipQuery,
  useGetOwnPhoneNumbersQuery,
  useGetTeamsQuery,
  useTransferCallMutation,
} from "@/store/rest_api";
import Membership from "@/model/interface/Membership";
import MemberAvailabilityStatus from "@/components/team/MemberAvailabilityStatus";
import { CallParticipant, CallTransferRequest, CallTransferTarget } from "@/model/interface/Call";
import Team from "@/model/interface/Team";
import { apiErrorMessage } from "@/util/apiError";
import { backLabel } from "@/util/availability";
import { getInitialsFromFullName } from "@/util/string";
import { memberName, memberRealName } from "@/util/member";
import { isUsE164, toUsE164 } from "@/util/validation";

interface TransferPickerProps {
  callId: string;
  leadName: string;
  mode: "TRANSFER" | "ADD";
  participants: CallParticipant[];
  onClose: () => void;
  onTransferPending: (isPending: boolean) => void;
  onTransferred: (targetName: string) => void;
}

const AVAILABILITY_POLL_MILLIS = 10000;

function memberLabel(member: Membership): string {
  return memberName(member, member.user?.email || "Teammate");
}

function requestFor(target: CallTransferTarget, note: string): CallTransferRequest {
  const trimmedNote = note.trim() || undefined;
  if (target.kind === "MEMBER") {
    return { targetMembership: { id: String(target.id) }, note: trimmedNote };
  }
  if (target.kind === "TEAM") {
    return { targetTeam: { id: String(target.id) }, note: trimmedNote };
  }
  if (target.kind === "PHONE_NUMBER") {
    return { targetPhoneNumber: { id: String(target.id) }, note: trimmedNote };
  }
  return { targetNumber: target.number, note: trimmedNote };
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-0.5 pb-0.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{children}</div>
  );
}

export default function TransferPicker({
  callId,
  leadName,
  mode,
  participants,
  onClose,
  onTransferPending,
  onTransferred,
}: TransferPickerProps) {
  const isTransfer = mode === "TRANSFER";
  const { data: members = [] } = useGetMemberAvailabilityQuery(undefined, {
    pollingInterval: AVAILABILITY_POLL_MILLIS,
  });
  const { data: ownMembership } = useGetOwnMembershipQuery();
  const { data: teams = [] } = useGetTeamsQuery();
  const { data: phoneNumbers = [] } = useGetOwnPhoneNumbersQuery(undefined, { skip: !isTransfer });
  const [transferCall, { isLoading: isTransferring }] = useTransferCallMutation();
  const [addCallParticipant, { isLoading: isAdding }] = useAddCallParticipantMutation();
  const [search, setSearch] = useState("");
  const [typedNumber, setTypedNumber] = useState("");
  const [note, setNote] = useState("");
  const [target, setTarget] = useState<CallTransferTarget | null>(null);

  const query = search.trim().toLowerCase();
  const availabilityById = new Map(members.map((member) => [String(member.id), member.availability]));
  const membershipIdsOnCall = new Set(
    participants
      .filter((participant) => participant.role === "AGENT" && participant.membership)
      .map((participant) => String(participant.membership?.id)),
  );
  const teammates = members.filter(
    (member) =>
      String(member.id) !== String(ownMembership?.id) &&
      !membershipIdsOnCall.has(String(member.id)) &&
      [memberLabel(member), memberRealName(member)].some((name) => name.toLowerCase().includes(query)),
  );
  const visibleTeams = isTransfer ? teams.filter((team) => team.name.toLowerCase().includes(query)) : [];
  const visibleNumbers = isTransfer
    ? phoneNumbers.filter((phoneNumber) =>
        `${phoneNumber.alias ?? ""} ${phoneNumber.number}`.toLowerCase().includes(query),
      )
    : [];
  const isSaving = isTransferring || isAdding;
  const firstName = target?.name.split(" ")[0] ?? "them";

  const teamNamesOf = (membershipId: string) =>
    teams
      .filter((team) => team.members.some((teamMember) => String(teamMember.membership.id) === membershipId))
      .map((team) => team.name)
      .join(", ");

  const teamCounts = (team: Team) => {
    const activeMembers = team.members.filter((teamMember) => availabilityById.has(String(teamMember.membership.id)));
    const available = activeMembers.filter((teamMember) => {
      const availability = availabilityById.get(String(teamMember.membership.id));
      return availability === "AVAILABLE" || availability === "MOBILE";
    });
    return `${available.length} of ${activeMembers.length} available`;
  };

  const teammateDetail = (member: Membership, membershipId: string) => {
    if (!member.availability || member.availability === "OFFLINE") {
      return "No app open";
    }
    if (member.availability === "UNAVAILABLE") {
      const changesAt = member.presence?.availabilityChangesAt;
      return changesAt ? backLabel(changesAt) : "Until they turn it back on";
    }
    if (member.availability === "MOBILE") {
      return "Rings their phone";
    }
    return teamNamesOf(membershipId) || "Teammate";
  };

  const isSelected = (kind: CallTransferTarget["kind"], id?: string) => target?.kind === kind && target.id === id;

  const chooseTypedNumber = () => {
    const number = toUsE164(typedNumber);
    setTypedNumber(number);
    if (isUsE164(number)) {
      setTarget({ kind: "NUMBER", number, name: number });
    } else if (target?.kind === "NUMBER") {
      setTarget(null);
    }
  };

  const submit = async () => {
    if (!target) {
      return;
    }
    try {
      if (isTransfer) {
        onTransferPending(true);
        await transferCall({ callId, data: requestFor(target, note) }).unwrap();
        onTransferred(target.name);
        return;
      }
      const data = target.kind === "MEMBER" ? { membership: { id: String(target.id) } } : { number: target.number };
      await addCallParticipant({ callId, data }).unwrap();
      toast.success(`Calling ${target.name}`);
      onClose();
    } catch (error) {
      if (isTransfer) {
        onTransferPending(false);
      }
      toast.error(apiErrorMessage(error, isTransfer ? "Could not transfer the call" : "Could not add them to the call"));
    }
  };

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-2 border-b px-3 py-2.5">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          aria-label="Back to the call"
          title="Back to the call"
          onClick={onClose}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1 truncate text-[15px] font-bold">
          {isTransfer ? `Transfer ${leadName}` : "Add to the call"}
        </div>
      </div>

      <div className="flex max-h-[520px] flex-col gap-3 overflow-y-auto px-4 pb-4 pt-3">
        <label className="flex h-[38px] items-center gap-2 rounded-lg border px-3 text-muted-foreground">
          <Search className="h-4 w-4 shrink-0" />
          <input
            type="search"
            aria-label={isTransfer ? "Search people, teams or numbers" : "Search people"}
            placeholder={isTransfer ? "Search people, teams or numbers" : "Search people"}
            value={search}
            onChange={(changeEvent) => setSearch(changeEvent.target.value)}
            className="flex-1 bg-transparent text-sm text-foreground outline-none"
          />
        </label>

        {teammates.length > 0 && (
          <div className="flex flex-col gap-1">
            <SectionLabel>Teammates</SectionLabel>
            {teammates.map((member) => {
              const membershipId = String(member.id);
              const name = memberLabel(member);
              const isOffline = !member.availability || member.availability === "OFFLINE";
              return (
                <button
                  key={membershipId}
                  type="button"
                  disabled={isOffline}
                  onClick={() => setTarget({ kind: "MEMBER", id: membershipId, name })}
                  className={cn(
                    "flex w-full items-center gap-2.5 rounded-lg border px-2.5 py-1.5 text-left",
                    isSelected("MEMBER", membershipId) ? "border-primary bg-primary/10" : "border-transparent",
                    isOffline ? "cursor-not-allowed opacity-55" : "hover:bg-muted/50",
                  )}
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                    {getInitialsFromFullName(name)}
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-sm font-bold">{name}</span>
                    <span className="truncate text-xs text-muted-foreground">
                      {teammateDetail(member, membershipId)}
                    </span>
                  </span>
                  <MemberAvailabilityStatus availability={member.availability} />
                </button>
              );
            })}
          </div>
        )}

        {visibleTeams.length > 0 && (
          <div className="flex flex-col gap-1">
            <SectionLabel>Teams</SectionLabel>
            {visibleTeams.map((team) => (
              <button
                key={team.id}
                type="button"
                onClick={() => setTarget({ kind: "TEAM", id: team.id, name: team.name })}
                className={cn(
                  "flex w-full items-center gap-2.5 rounded-lg border px-2.5 py-1.5 text-left hover:bg-muted/50",
                  isSelected("TEAM", team.id) ? "border-primary bg-primary/10" : "border-transparent",
                )}
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
                  <Users className="h-4 w-4" />
                </span>
                <span className="flex-1 truncate text-sm font-bold">{team.name}</span>
                <span className="shrink-0 text-xs font-semibold tabular-nums text-muted-foreground">
                  {teamCounts(team)}
                </span>
              </button>
            ))}
          </div>
        )}

        {visibleNumbers.length > 0 && (
          <div className="flex flex-col gap-1">
            <SectionLabel>Our numbers</SectionLabel>
            {visibleNumbers.map((phoneNumber) => {
              const phoneNumberId = String(phoneNumber.id);
              const name = phoneNumber.alias || phoneNumber.number;
              return (
                <button
                  key={phoneNumberId}
                  type="button"
                  onClick={() => setTarget({ kind: "PHONE_NUMBER", id: phoneNumberId, name })}
                  className={cn(
                    "flex w-full items-center gap-2.5 rounded-lg border px-2.5 py-2 text-left hover:bg-muted/50",
                    isSelected("PHONE_NUMBER", phoneNumberId) ? "border-primary bg-primary/10" : "border-transparent",
                  )}
                >
                  <span className="flex-1 truncate text-sm font-bold">{name}</span>
                  <span className="shrink-0 text-xs font-semibold tabular-nums text-muted-foreground">
                    {phoneNumber.number}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="transfer-number"
            className="px-0.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground"
          >
            Any US number
          </label>
          <Input
            id="transfer-number"
            type="tel"
            placeholder="+1 555 010 0199"
            value={typedNumber}
            onChange={(changeEvent) => setTypedNumber(changeEvent.target.value)}
            onBlur={chooseTypedNumber}
            onKeyDown={(keyEvent) => {
              if (keyEvent.key === "Enter") {
                chooseTypedNumber();
              }
            }}
            className={cn("tabular-nums", target?.kind === "NUMBER" && "border-primary")}
          />
          {typedNumber && !isUsE164(toUsE164(typedNumber)) && (
            <p className="text-xs text-destructive">Enter a US number, for example +1 555 010 0199.</p>
          )}
        </div>

        {isTransfer && target && (
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="transfer-note"
              className="px-0.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground"
            >
              Note for {firstName}
            </label>
            <Textarea
              id="transfer-note"
              rows={2}
              value={note}
              onChange={(changeEvent) => setNote(changeEvent.target.value)}
              className="resize-none"
            />
          </div>
        )}

        <Button type="button" className="mt-1 h-10" disabled={!target || isSaving} onClick={submit}>
          {target ? `${isTransfer ? "Transfer to" : "Add"} ${firstName}` : isTransfer ? "Choose who takes the call" : "Choose who to add"}
        </Button>
      </div>
    </div>
  );
}
